import { useEffect, useRef } from 'react'

/** The Keenfolks K mark's three strokes, in its 256×256 coordinate space. */
const K_PATHS = [
	'M14,16 H58 V144 L14,188 Z',
	'M196,16 H240 V78 L78,240 H14 L196,58 Z',
	'M241,240 H196 V184 L133,121 L165,89 L241,164 Z',
] as const

const W = 480
const H = 320
const CELL = 10
const COLS = Math.floor(W / CELL)
const ROWS = Math.floor(H / CELL)
/** The mark's rendered edge length inside the canvas, centered. */
const MARK = 272
const RAMP = ' ·∙•◦○●◉█'.split('')

/** Rasterize the K once and mark which ASCII cells its strokes cover. */
function sampleMask(): boolean[] {
	const off = document.createElement('canvas')
	off.width = W
	off.height = H
	const c = off.getContext('2d')
	if (!c) return []
	const k = MARK / 256
	c.setTransform(k, 0, 0, k, (W - MARK) / 2, (H - MARK) / 2)
	// Any opaque colour does: this canvas is never displayed and only its alpha
	// channel is sampled below, so no design token is involved.
	c.fillStyle = '#000'
	for (const d of K_PATHS) c.fill(new Path2D(d))
	const px = c.getImageData(0, 0, W, H).data
	const mask: boolean[] = []
	for (let row = 0; row < ROWS; row++)
		for (let col = 0; col < COLS; col++) {
			const x = Math.min(W - 1, col * CELL + CELL / 2) | 0
			const y = Math.min(H - 1, row * CELL + CELL / 2) | 0
			mask.push(px[(y * W + x) * 4 + 3] > 140)
		}
	return mask
}

/**
 * The hero's Keenfolks K, rendered in the Atlas ToBe ASCII style: the mark's
 * cells shimmer through a mono density ramp and glow around the pointer, in
 * the same grid/ramp/interaction language as the mockup's `#ascii3d` canvas.
 * Honors reduced motion by settling to a still frame.
 */
export function AsciiKMark({ className }: { className?: string }) {
	const canvasRef = useRef<HTMLCanvasElement | null>(null)

	useEffect(() => {
		const canvas = canvasRef.current
		const ctx = canvas?.getContext('2d')
		if (!canvas || !ctx) return
		const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false

		const dpr = window.devicePixelRatio || 1
		canvas.width = W * dpr
		canvas.height = H * dpr
		ctx.scale(dpr, dpr)

		const mask = sampleMask()
		const mouse = { x: -9999, y: -9999, active: false }
		let t = 0

		const onMove = (e: PointerEvent) => {
			const r = canvas.getBoundingClientRect()
			// The canvas can display smaller than its logical size, so map into it.
			mouse.x = ((e.clientX - r.left) / r.width) * W
			mouse.y = ((e.clientY - r.top) / r.height) * H
			mouse.active = true
		}
		const onLeave = () => {
			mouse.active = false
		}
		canvas.addEventListener('pointermove', onMove)
		canvas.addEventListener('pointerleave', onLeave)

		const mono = getComputedStyle(canvas).fontFamily || 'monospace'

		const draw = () => {
			ctx.clearRect(0, 0, W, H)
			ctx.font = `${CELL}px ${mono}`
			ctx.textBaseline = 'middle'
			ctx.textAlign = 'center'

			for (let row = 0; row < ROWS; row++)
				for (let col = 0; col < COLS; col++) {
					const ink = mask[row * COLS + col]
					const px = col * CELL + CELL / 2
					const py = row * CELL + CELL / 2
					const dm = Math.hypot(px - mouse.x, py - mouse.y)
					const infl = mouse.active ? Math.max(0, 1 - dm / 150) : 0
					if (!ink && infl < 0.05) continue
					// A slow diagonal wave keeps the mark alive; the pointer brightens it locally.
					const drift = ink ? 0.5 + 0.5 * Math.sin((col + row) * 0.4 + t * 0.04) : 0
					const intensity = Math.min(1, ink ? 0.45 + 0.35 * drift + 0.5 * infl : infl * 0.35)
					const ch = RAMP[Math.min(RAMP.length - 1, Math.floor(intensity * RAMP.length))]
					if (infl > 0.55 && ink) ctx.fillStyle = `rgba(255,255,255,${0.7 + infl * 0.3})`
					else if (ink) ctx.fillStyle = `rgba(255,255,255,${0.3 + intensity * 0.5})`
					else ctx.fillStyle = `rgba(255,255,255,${intensity * 0.6})`
					ctx.fillText(ch, px, py)
				}
			t++
		}

		if (reduce) {
			draw()
		}
		const interval = reduce ? null : window.setInterval(draw, 33)
		return () => {
			if (interval !== null) window.clearInterval(interval)
			canvas.removeEventListener('pointermove', onMove)
			canvas.removeEventListener('pointerleave', onLeave)
		}
	}, [])

	return <canvas ref={canvasRef} aria-hidden className={`cursor-crosshair font-mono ${className ?? ''}`} />
}
